// @flow
import React, { useContext, useMemo } from 'react';
import moment from 'moment-timezone';
import prop from 'ramda/es/prop';
import pipe from 'ramda/es/pipe';
import values from 'ramda/es/values';
import map from 'ramda/es/map';
import unnest from 'ramda/es/unnest';
import filter from 'ramda/es/filter';
import { Scrollbars } from 'react-custom-scrollbars-2';

import toFixedValue from 'app/common/lib/toFixedValue';
import Loading from 'app/common/components/Loader';
import { Table, Tr, Td, Th } from 'app/common/ui-components/table';
import { useCurrencySign } from 'app/common/components-next/currency';

import { SubsidiesContext, type Subsidy } from '../../CafeteriaSubsidiesData';
import { SubsidyOrdersContext, type SubsidyOrder } from '../../CafeteriaSubsidyOrdersData';
import { Purchases } from '../../PurchasesApi';
import IconSubsidyOrder from './icons/subsidy-order.svg';
import styles from './ItemDetails.module.scss';;


type Props = {
  date: string,
  timezone: string,
}

const ItemDetails = ({ date, timezone }: Props) => {
  const currency = useCurrencySign();

  /**
   *  Дотации
   */

  const {
    content: { subsidies },
    loading: { subsidies: loadingSubsidies },
  } = useContext(SubsidiesContext);

  const { order_id: orderId, spent, total, order_number: orderNumber }: Subsidy = subsidies[date] || {};

  const [header1, header2] = useMemo(() => ([
    `Детализация дотаций за ${moment(date).format('DD.MM.YYYY')}`,
    `Итого: ${toFixedValue(spent || 0)}/${toFixedValue(total || 0)}\u00A0${currency}`,
  ]), [spent, total, date, currency]);

  /**
   *  Справки
   */

  const {
    content: { subsidyOrders },
    loading: { subsidyOrders: loadingSubsidyOrders },
  } = useContext(SubsidyOrdersContext);

  const {
    valid_from: validFrom,
    valid_to: validTo,
    per_day: perDay,
  }: SubsidyOrder = prop(orderId, subsidyOrders) || {};

  const sPeriod = useMemo(() => {
    const sFrom = moment(validFrom).format('DD.MM.YYYY');
    const sTo = moment(validTo).format('DD.MM.YYYY');
    return `${sFrom}–${sTo}`;
  }, [validFrom, validTo]);

  /**
   *  Заказы
   */

  const { content: { purchases }, loading: loadingPurchases } = useContext(Purchases);
  const orders = useMemo(() => pipe(
    values,
    map(({ datetime, items }) => {
      const mDate = moment(datetime).tz(timezone);
      return map(({ id, title, n: count, subsidy }) => ({
        id,
        time: mDate.format('H:mm'),
        title,
        count,
        subsidy: subsidy ? toFixedValue(subsidy) : '',
      }), values(items));
    }),
    unnest,
    filter(({ subsidy }) => !!subsidy),
  )(purchases), [purchases, timezone]);

  const loading = loadingSubsidies || loadingSubsidyOrders || loadingPurchases;

  return (
    <article className={styles.root}>
      <h3 className={styles.header}>
        <span className={styles.headerLeft}>{header1}</span>
        <span className={styles.headerRight}>{header2}</span>
      </h3>

      {!loadingSubsidyOrders
        && <div className={styles.ref}>
          <div className={styles.refNum}>
            <IconSubsidyOrder className={styles.refIcon} />
            <span className={styles.gray}>
              {'№'}
              &nbsp;
              &nbsp;
            </span>
            <span className={styles.primary}>{orderNumber}</span>
          </div>
          <div className={styles.refDescription}>
            <div className={styles.refDuration}>
              <span className={styles.refAnot}>
                {'Срок действия'}
              </span>
              {sPeriod}
            </div>
            <div>
              <span className={styles.refAnot}>
                {`Доступно в день ${currency}`}
              </span>
              {toFixedValue(perDay || 0)}
            </div>
          </div>
        </div>}

      {loading && <Loading />}

      {!loading
        && <Scrollbars className={styles.scrollbars}>
          <Table className={styles.table}>
            <thead>
              <Tr header>
                <Th alignLeft className={styles.th}>{'№'}</Th>
                <Th alignLeft className={styles.th}>{'Время'}</Th>
                <Th alignLeft className={styles.th}>{'Наименование'}</Th>
                <Th alignLeft className={styles.th}>{'Кол-во'}</Th>
                <Th alignLeft className={styles.th}>{`Сумма,\u00A0${currency}`}</Th>
              </Tr>
            </thead>
            <tbody>
              {orders.map(({ id, time, title, count, subsidy }) => (
                <Tr key={id}>
                  <Td>{id}</Td>
                  <Td>{time}</Td>
                  <Td>{title}</Td>
                  <Td>{count}</Td>
                  <Td>{subsidy}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Scrollbars>}
    </article>
  );
};

export default ItemDetails;
